import { createContext, ReactNode, useState, useEffect } from 'react';
import { clearToken, getToken, setToken as persistToken, removeToken } from '../services/auth';
import * as jwt_decode from 'jwt-decode';
import { EUserRole } from '../types/enums';
import { resetYoutubeReconnectPromptFlag } from '../services/youtubeOwnerConnection';

interface DecodedToken {
  email: string;
  id: string;
  role: EUserRole;
  exp: number;
  iat: number;
}

type AuthContextData = {
  authInfo: DecodedToken | null;
  isAuthResolved: boolean;
  setToken: (token: string) => void;
};

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthContext = createContext({} as AuthContextData);

export function AuthProvider({ children }: AuthProviderProps) {
  const [authInfo, setAuthInfo] = useState<DecodedToken | null>(null);
  const [isAuthResolved, setIsAuthResolved] = useState(false);

  const validateAndSetToken = (
    token: string,
    options: { clearInvalid: boolean } = { clearInvalid: true },
  ): boolean => {
    try {
      const decoded: DecodedToken = jwt_decode.jwtDecode(token);
      const now = Date.now() / 1000;
      
      if (decoded.exp > now && decoded.role) {
        setAuthInfo(decoded);
        return true;
      }
    } catch (error) {
      console.warn('Token validation failed:', error);
    }
    setAuthInfo(null);
    if (options.clearInvalid) {
      removeToken();
    } else {
      clearToken();
    }
    return false;
  };

  const setTokenHandler = (token: string) => {
    persistToken(token);
    validateAndSetToken(token);
    setIsAuthResolved(true);
    resetYoutubeReconnectPromptFlag();
  };

  useEffect(() => {
    const token = getToken();
    if (token) {
      validateAndSetToken(token, { clearInvalid: false });
    }
    setIsAuthResolved(true);
  }, []);

  return (
    <AuthContext.Provider value={{ authInfo, isAuthResolved, setToken: setTokenHandler }}>
      {children}
    </AuthContext.Provider>
  );
}
